import { Checkbox, Input, Select } from "antd";
import type { CheckboxChangeEvent } from "antd/es/checkbox";
import { SizeType } from "antd/es/config-provider/SizeContext";
import { useMemo, useState } from "react";
import SelectDownIcon from "../../../../assets/icons/SelectDownIcon";

const { Option } = Select;

type Item = { label: string; opLabel?: string | number; value: number | string };

type Props = {
  list: Item[];
  disabled?: boolean;
  placeholder?: string;
  value?: (string | number)[];
  onChange?: (val: (string | number)[]) => void;
  size?: SizeType;
  suffixIcon?: React.ReactNode;
  maxTagCount?: number;
};

export default function ListWithCheckbox({
  disabled = false,
  placeholder = "Select",
  list,
  value = [],
  onChange,
  size = "middle",
  suffixIcon = <SelectDownIcon />,
  maxTagCount = 1,
}: Props) {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredData = useMemo(() => {
    const lower = searchTerm.toLowerCase();
    return list.filter(
      (el) =>
        String(el?.label || "")
          .toLowerCase()
          .includes(lower) ||
        String(el?.opLabel || "")
          .toLowerCase()
          .includes(lower)
    );
  }, [list, searchTerm]);

  const isAllSelected =
    filteredData.length > 0 &&
    filteredData.every((emp) => value.includes(emp.value));

  const handleSelectAll = (e: CheckboxChangeEvent) => {
    if (e.target.checked) {
      const filteredIds = filteredData.map((emp) => emp.value);
      const unique = Array.from(new Set([...value, ...filteredIds]));
      onChange?.(unique);
    } else {
      const remaining = value.filter(
        (id) => !filteredData.find((emp) => emp.value === id)
      );
      onChange?.(remaining);
    }
  };

  const handleCheckboxChange = (id: number | string, checked: boolean) => {
    const newSelected = checked
      ? [...value, id]
      : value.filter((v) => v !== id);
    onChange?.(newSelected);
  };

  return (
    <Select
      placeholder={placeholder}
      mode="tags"
      value={value}
      disabled={disabled}
      onChange={(val) => onChange?.(val)}
      size={size}
      suffixIcon={suffixIcon}
      allowClear
      maxTagCount={maxTagCount}
      dropdownRender={() => (
        <div className="p-2">
          <Checkbox
            onChange={handleSelectAll}
            checked={isAllSelected}
            disabled={!filteredData.length}
            className="mb-2 flex items-center"
          >
            {isAllSelected ? "Deselect all" : "Select all"}
          </Checkbox>
          <Input
            placeholder="Search by name or ID"
            onChange={(e) => setSearchTerm(e.target.value)}
            className="mb-2"
            size={size}
          />
          <div className="max-h-40 overflow-y-auto">
            {filteredData.map((el) => (
              <div key={el.value}>
                <Checkbox
                  checked={value.includes(el.value)}
                  onChange={(e) =>
                    handleCheckboxChange(el.value, e.target.checked)
                  }
                  className="flex items-center py-1"
                >
                  {`${el.label} (${el.opLabel})`}
                </Checkbox>
              </div>
            ))}
          </div>
        </div>
      )}
      tagRender={({ label }) => (
        <div className="bg-[#f0f0f0] rounded-md text-body mr-1 px-2 py-[5px]">
          {list.find((el) => el.value === label)?.label || label}
        </div>
      )}
    >
      {/* Hidden real options just for value tracking */}
      {list.map((el) => (
        <Option key={el.value} value={el.value}>
          {el.label}
        </Option>
      ))}
    </Select>
  );
}
